import { useReactiveVar } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { UserRoleEnum } from '@/types/graphql';
import { currentAdminVar } from '@/util/apollo/cache';

import {
  extractPolicyApiError,
  firstResponderConsoleAPI,
} from '@/services/firstResponderConsoleAPI';

import * as styles from '../FirstResponder.module.css';
import {
  mapPolicy,
  mapPolicyAudits,
  toAttentionPage,
  toClientFilters,
  toDiagnostics,
  toJobDetail,
  toJobRow,
  toPagination,
  toResults,
  toSafety,
  toStats,
  toWorkersPage,
} from '../Live/liveWireAdapter';
import type { FirstResponderPolicyWire } from '../Live/liveWireContract';
import LiveConsole, {
  OpenJob,
  SettingsProps,
} from '../LivePreview/LiveConsole';
import { ModeReasonTarget } from '../LivePreview/ModeReasonModal';
import { UnauthorizedReason } from '../LivePreview/StateShell';
import {
  LiveConsoleData,
  LiveJobDetail,
  LiveWorkerRow,
  LivePage,
  LivePilotPolicy,
  LiveRankingExplainer,
  LiveStopCopy,
} from '../LivePreview/liveViewModel';
import ConnectedPolicyEditor, { SubmitResult } from './ConnectedPolicyEditor';

/*
 * GRAVY-10373 canonical UI convergence — the ONE production First Responder container. Owns every
 * real data source: console/policy/audits/jobs/job-detail/workers/attention (Task 7 — the full
 * contract, via Live/liveWireAdapter.ts) and the real mode/stop transitions (Task 3). Renders the
 * exact same shared LiveConsole shell the frozen preview renders (Presentation/*), fed by
 * real-data adapter output instead of fixtures — never a second, independent console
 * presentation (see FRONTEND_CONTRACT_RECONCILIATION.md / ROUTE_RETIREMENT_PLAN.md for why this
 * replaces Connected/ConnectedConsole.tsx, which is now dead code awaiting Phase 10 deletion).
 *
 * Task 3: the OFF<->SHADOW-only audited-feature-flag PATCH (experimentsAPI.updateFlag /
 * FIRST_RESPONDER_FLAG_KEY) is retired from this page. Every mode transition — Start Shadow, Go
 * Live, Back to Shadow, and Stop — now goes through the one real mode endpoint
 * (firstResponderConsoleAPI.setMode), each carrying an operator-typed reason. Never optimistic:
 * a 200 is followed by refetchAfterMutation() before anything displayed changes; a failure
 * leaves the prior consoleWire (and thus the displayed mode) untouched and surfaces the real
 * message via bannerSlot.
 *
 * Task 7: the legacy overview endpoint (firstResponderAPI.getOverview/getJobDetail) and
 * Presentation/legacyJobsAdapter.ts are gone from this page. Jobs, job detail, workers and
 * attention are all real, contract-shaped, server-paginated data:
 *   - Jobs: selectedFilter -> customer_id query; page changes call getJobs; rows/pagination are
 *     the server's, never sliced client-side.
 *   - Job modal: getJob(jobId) on open, cached by id. A real 'watching' row (no persisted run —
 *     GET .../jobs/:id would 404 for it) is never clickable in the first place (JobsCard.tsx).
 *   - Workers tab: getWorkers(jobId, page, perPage) with real server paging.
 *   - Attention card: getAttention(1, 25) — a real collection, never derived from job rows.
 *   - Diagnostics: entirely toDiagnostics(consoleWire) — the mode-scoped shadow_invariant merge
 *     (toShadowScopedDiagnostics) is gone with the legacy overview endpoint it read.
 */

const AUDITS_PER_PAGE = 25;
const JOBS_PER_PAGE = 10;
const WORKERS_PER_PAGE = 25;
const ATTENTION_PER_PAGE = 25;

const EMPTY_JOBS_PAGE: LivePage<ReturnType<typeof toJobRow>> = {
  items: [],
  pagination: { total: 0, page: 1, perPage: JOBS_PER_PAGE, totalPages: 1 },
};

type LoadStatus = 'loading' | 'error' | 'unauthorized' | 'ready';

// F9: 403 is an authorization verdict exactly as 401 is, and it belongs in the unauthorized frame
// with it. Mapping it to the generic 'error' status gave the operator a Retry that re-ran the
// identical forbidden request and failed identically — a dead control dressed as a recovery.
// Every OTHER status stays 'error', where a retry genuinely can succeed.
//
// The REASON travels with the status, because the two codes need opposite instructions: a 401 is
// a dead session the operator fixes by signing in again, a 403 is a role verdict that signing in
// again cannot change. Collapsing them into one sentence would just relocate the misleading
// generic error. `reason` is null for 'error', which has no unauthorized copy to choose.
const statusFromError = (
  err: any,
): {
  status: 'unauthorized' | 'error';
  reason: UnauthorizedReason | null;
} => {
  const status = err?.response?.status;
  if (status === 401) return { status: 'unauthorized', reason: 'session' };
  if (status === 403) return { status: 'unauthorized', reason: 'role' };
  return { status: 'error', reason: null };
};

const codeFromError = (err: any): 400 | 401 | 403 | 409 | 422 | 'network' => {
  const status = err?.response?.status;
  return status === 400 ||
    status === 401 ||
    status === 403 ||
    status === 409 ||
    status === 422
    ? status
    : 'network';
};

// Sole owner of Idempotency-Key generation. Prefers native crypto.randomUUID(); falls back to
// uuid's v4() when it's unavailable (secure-context gap on local-dev hostnames). No global
// polyfill, no Math.random.
const createIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : uuidv4();

// Static, policy-derived copy (not backend data — mirrors LivePreview/derive.ts's own pattern of
// hand-written prose describing the frozen, verified ranking policy and Stop semantics). Owned
// here (this container is the sole consumer) now that Presentation/legacyJobsAdapter.ts is gone.
const REAL_RANKING_EXPLAINER: LiveRankingExplainer = {
  intro:
    'Candidates are ranked by ranking version r2 of the reliability_first policy: dependability tier first, then distance band, then relationship, then the higher dependability score, then the closer distance, with worker id only as the last resort.',
  steps: [
    'Dependability tier — Platinum, Gold, Standard, then Monitored/Unknown.',
    'Distance band — closer bands rank first within the same tier.',
    'Relationship — a prior working relationship with this client breaks remaining ties.',
    'Higher dependability score — breaks ties inside the same tier, band and relationship; a missing score sorts last.',
    'Closer distance — exact miles, only when the score also ties.',
    'Worker id — a stable, arbitrary tie-break of last resort so ranking is always deterministic.',
  ],
  scoreNote:
    'The score breaks ties inside a tier and distance band; it never outranks a tier or a band.',
  unknownNote:
    'A worker with unknown dependability sorts after every scored tier, never before.',
};

// F9: Stop copy is mode-aware. From LIVE, "Shadow never contacts a worker either way" is false —
// LIVE offers do contact workers — so LIVE gets its own truthful inflight sentence about what
// already-sent offers and an in-progress tick actually do. From Shadow (and OFF, where Stop is
// unreachable anyway — see stopEnabled in LiveConsole.tsx), today's copy stays exactly as it was.
const REAL_STOP_COPY_LIVE: LiveStopCopy = {
  title: 'Stop First Responder?',
  body: 'This turns off the audited feature flag. No new evaluations will run until it is turned back on.',
  inflight:
    'Offers already sent stay valid and workers can still accept them. A tick that is already running finishes only the job it is on.',
  sub: 'Historical runs and reporting stay exactly as they are; nothing is deleted or reverted by stopping.',
};

const REAL_STOP_COPY_SHADOW: LiveStopCopy = {
  title: 'Stop First Responder?',
  body: 'This turns off the audited feature flag. No new evaluations will run until it is turned back on.',
  inflight:
    'An evaluation already in flight when you stop may still finish recording its decision — Shadow never contacts a worker either way.',
  sub: 'Historical runs and reporting stay exactly as they are; nothing is deleted or reverted by stopping.',
};

const ConnectedFirstResponder = () => {
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  // F9: which kind of "not allowed" the last failure was. Only read while status is
  // 'unauthorized', and every path that reaches that status states the reason explicitly
  // (statusFromError maps 401 -> 'session', 403 -> 'role').
  //
  // M9 (independent review): the initial value is 'session', not 'role'. The default only ever
  // shows if something reaches the frame without a verdict, and the failure that actually happens
  // on this console is the 401 whose token refresh gave up — a role that may not read the console
  // is refused at the route long before this container mounts. Guessing 'role' sent the common
  // case to a fix that is not theirs to make.
  const [unauthorizedReason, setUnauthorizedReason] =
    useState<UnauthorizedReason>('session');
  const [consoleWire, setConsoleWire] = useState<any>(null);
  const [policyWire, setPolicyWire] = useState<FirstResponderPolicyWire | null>(
    null,
  );
  const [auditsWire, setAuditsWire] = useState<any>(null);
  const [auditPageNum, setAuditPageNum] = useState(1);
  // F8: the audit page's own version of the same silent failure — a `.catch(() => {})`. Rendered
  // next to the audit table (PilotSettingsSection), with a Retry that re-requests that page.
  const [auditPageError, setAuditPageError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [confirmationStale, setConfirmationStale] = useState(false);

  // Task 7 — real, server-paginated jobs. `jobsQueryRef` is the single source of truth for
  // "the current jobs page" (customer_id + page) that `load`/`refetchAfterMutation` read at call
  // time via a ref (never a stale closure, never widening those callbacks' own dependency arrays).
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [jobsPageNum, setJobsPageNum] = useState(1);
  const [jobsWire, setJobsWire] = useState<any>(null);
  const [jobsLoading, setJobsLoading] = useState(false);
  // F8: a failed jobs page fetch used to be an empty catch — the spinner stopped, the previous
  // page stayed, and nothing said the backend had refused, so the click simply looked dead. The
  // message is rendered as JobsCard's own inline notice (the M1 pattern), with a Retry that
  // re-runs the exact request that failed (jobsQueryRef.current, never a whole-console reload).
  const [jobsPageError, setJobsPageError] = useState<string | null>(null);
  const jobsQueryRef = useRef<{ customerId: number | null; page: number }>({
    customerId: null,
    page: 1,
  });
  // Fix round 1 (stale-response race): monotonically increasing ids, one per collection. Whichever
  // call STARTED last wins — a response is only applied if its own id is still the latest one when
  // it resolves, so two overlapping fetches of the SAME collection (a foreground filter/page change
  // racing a background load()/refetchAfterMutation(), or two of either) can never have the earlier
  // one clobber the later one just because its network round trip happened to finish first. Shared
  // across every call site that can populate that collection (fetchJobsPage, load,
  // refetchAfterMutation) — never scoped to just one of them, since the race is BETWEEN them.
  // Mirrors the cancelled-flag pattern the job-detail/workers effects already use, adapted for
  // ref-driven callbacks rather than an effect cleanup.
  const jobsRequestIdRef = useRef(0);
  const attentionRequestIdRef = useRef(0);
  // M4: the audit page had no such guard at all — onAuditPageChange applied whatever resolved,
  // in whatever order. Same shared-counter pattern as jobs/attention above, spanning every call
  // site that can populate auditsWire (load, refetchAfterMutation, onAuditPageChange).
  const auditsRequestIdRef = useRef(0);
  // Fix round 2 (regression from round 1's guard): a SEPARATE counter, incremented ONLY by
  // fetchJobsPage, that tracks fetchJobsPage's own lifecycle for `jobsLoading` alone. The shared
  // `jobsRequestIdRef` above answers "is this response still wanted for jobsWire" across ALL three
  // fetchers; it must NOT also gate jobsLoading, because a background load()/refetchAfterMutation()
  // legitimately bumps that shared counter past an in-flight fetchJobsPage call's id — and under
  // the old single-counter design, fetchJobsPage's own `finally` then saw its id was no longer
  // "current" and skipped setJobsLoading(false) too, stranding the Jobs card in "Loading jobs…"
  // forever (nothing else ever resets it, and the controls that would retrigger a fetch are
  // themselves disabled while it's true). jobsLoading must only ever depend on whether a NEWER
  // fetchJobsPage call has started — never on whether some unrelated background refetch has.
  const jobsPageFetchIdRef = useRef(0);

  // Task 7 — real attention endpoint (never derived from job rows).
  const [attentionWire, setAttentionWire] = useState<any>(null);

  // Task 7 — job modal: real detail (cached by job id) + real, server-paginated workers.
  const [openJob, setOpenJob] = useState<OpenJob>(null);
  const [jobDetailsById, setJobDetailsById] = useState<
    Record<string, LiveJobDetail>
  >({});
  const [workersPageNum, setWorkersPageNum] = useState(1);
  const [workersPage, setWorkersPage] = useState<
    (LivePage<LiveWorkerRow> & { candidatesTotal: number | null }) | null
  >(null);
  // M1: a failed getJob/getWorkers used to be a silent dead click (an empty catch — the modal
  // simply never opened, with no feedback at all). Now surfaced as a small inline error in the
  // jobs card, with a retry that re-attempts whichever call(s) haven't yet succeeded for the open
  // job. jobLoadRetryTick is bumped by Retry to force both effects to re-run.
  const [jobLoadError, setJobLoadError] = useState<string | null>(null);
  const [jobLoadRetryTick, setJobLoadRetryTick] = useState(0);
  // Bumped by refetchAfterMutation: every mutation that refetches the console also refetches the
  // open job's detail, so a tick can never leave the modal describing a run it has moved on.
  const [jobDetailsVersion, setJobDetailsVersion] = useState(0);
  const jobDetailsRef = useRef(jobDetailsById);
  jobDetailsRef.current = jobDetailsById;

  // Task 3 — mode/stop via the real mode endpoint.
  const [stopOpen, setStopOpen] = useState(false);
  const [modeReasonTarget, setModeReasonTarget] =
    useState<ModeReasonTarget | null>(null);
  // GRAVY-10816 bug 2: Helpdesk may open this console (routes/index.tsx admits the role, per AC-6)
  // but not operate it. Keyed on user.roleId, the same field PrivateRoute and DiagnosticsSection's
  // tick gate use; a KNOWN role that is not Tenant Admin gets the page read-only, and the
  // backend's require_tenant_admin_role is the real authority behind every write regardless.
  //
  // S5 (independent review): an UNKNOWN role is not a read-only one. `currentAdminVar` is written
  // from an effect in Authenticated, so it is still null on the first render after the account
  // query resolves — and keying this on "is Tenant Admin" made that frame render a Read-only
  // badge and NO STOP BUTTON at a real tenant admin. Stop is the kill switch: it must never be
  // hidden, not even for a frame, by a race over who the operator is. So the unresolved state
  // OPERATES, and the two authorities that can actually refuse are left to do it — the backend
  // answers a write from the wrong role with a 403, surfaced inline like any other refusal, and
  // "Run one tick now" keeps its own fail-closed gate in DiagnosticsSection, where the cost of
  // showing a GravyWork-internal control to the wrong reader outranks a frame without it.
  const currentAdmin = useReactiveVar(currentAdminVar);
  const roleId = currentAdmin?.user?.roleId;
  const canOperate = !roleId || roleId === UserRoleEnum.TENANT_ADMIN;
  const [modePending, setModePending] = useState(false);
  const [modeError, setModeError] = useState<string | null>(null);

  // F11 — "Run one tick now": lets an operator tick a review app that has no scheduler, and
  // doubles as the demo control. Independent of modePending/modeError above (a tick is not a mode
  // transition), same pending/result pattern otherwise.
  const [runTickPending, setRunTickPending] = useState(false);
  const [runTickResult, setRunTickResult] = useState<string | null>(null);

  const load = useCallback(async (page = auditPageNum) => {
    setStatus('loading');
    try {
      const { customerId, page: jobsPage } = jobsQueryRef.current;
      const jobsRequestId = (jobsRequestIdRef.current += 1);
      const attentionRequestId = (attentionRequestIdRef.current += 1);
      const auditsRequestId = (auditsRequestIdRef.current += 1);
      const [c, p, a, j, att] = await Promise.all([
        firstResponderConsoleAPI.getConsole(),
        firstResponderConsoleAPI.getPolicy(),
        firstResponderConsoleAPI.getAudits(page, AUDITS_PER_PAGE),
        firstResponderConsoleAPI.getJobs(customerId, jobsPage, JOBS_PER_PAGE),
        firstResponderConsoleAPI.getAttention(1, ATTENTION_PER_PAGE),
      ]);
      setConsoleWire(c);
      setPolicyWire(p);
      // M4 stale-response guard: only apply if nothing newer has started.
      if (auditsRequestId === auditsRequestIdRef.current) setAuditsWire(a);
      // Stale-response guard (Fix round 1, FINDING 1): only apply if nothing newer has started.
      if (jobsRequestId === jobsRequestIdRef.current) setJobsWire(j);
      if (attentionRequestId === attentionRequestIdRef.current)
        setAttentionWire(att);
      setStatus('ready');
      setConfirmationStale(false);
    } catch (err) {
      const { status: nextStatus, reason } = statusFromError(err);
      setStatus(nextStatus);
      // Left untouched by a non-auth failure: 'error' carries no unauthorized copy to choose, and
      // overwriting it would have the frame describe a different failure than the one that showed
      // it, should a later 401/403 arrive with its own reason.
      if (reason) setUnauthorizedReason(reason);
      setErrorMessage(
        extractPolicyApiError(err, 'Failed to load First Responder console')
          .message,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refreshes console+policy+audits+the current jobs page+attention without dropping to a
  // full-screen loading state. Returns whether it actually succeeded — callers use this to decide
  // whether the mutation (a policy save/activate, or a mode/stop flag change) they're following up
  // on can be reported as fully confirmed.
  const refetchAfterMutation = useCallback(async (): Promise<boolean> => {
    try {
      const { customerId, page: jobsPage } = jobsQueryRef.current;
      const jobsRequestId = (jobsRequestIdRef.current += 1);
      const attentionRequestId = (attentionRequestIdRef.current += 1);
      const auditsRequestId = (auditsRequestIdRef.current += 1);
      const [c, p, a, j, att] = await Promise.all([
        firstResponderConsoleAPI.getConsole(),
        firstResponderConsoleAPI.getPolicy(),
        firstResponderConsoleAPI.getAudits(auditPageNum, AUDITS_PER_PAGE),
        firstResponderConsoleAPI.getJobs(customerId, jobsPage, JOBS_PER_PAGE),
        firstResponderConsoleAPI.getAttention(1, ATTENTION_PER_PAGE),
      ]);
      setConsoleWire(c);
      setPolicyWire(p);
      // M4 stale-response guard: only apply if nothing newer has started — this is exactly the
      // race a foreground audit-page click racing this background refetch could hit (see
      // Connected/__tests__/index.test.tsx's dedicated test for it).
      if (auditsRequestId === auditsRequestIdRef.current) setAuditsWire(a);
      // Stale-response guard (Fix round 1, FINDING 1): only apply if nothing newer has started —
      // this is exactly the race a foreground filter/page click racing this background refetch
      // could hit (see Connected/__tests__/index.test.tsx's dedicated test for it).
      if (jobsRequestId === jobsRequestIdRef.current) setJobsWire(j);
      if (attentionRequestId === attentionRequestIdRef.current)
        setAttentionWire(att);
      // Any mutation can move the open job's run on (review round 1): refetch its detail too.
      setJobDetailsVersion((v) => v + 1);
      return true;
    } catch {
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditPageNum]);

  const onSaveDraft = useCallback(
    async (policy: LivePilotPolicy, reason: string): Promise<SubmitResult> => {
      if (confirmationStale) {
        return {
          ok: false,
          code: 'network',
          errors: ['Reload the console before making another change.'],
        };
      }
      const idempotencyKey = createIdempotencyKey();
      const basedOnVersion = policyWire?.active?.version ?? '';
      const expectedDraftVersion = policyWire?.draft
        ? policyWire.draft.policy.version
        : null;
      try {
        await firstResponderConsoleAPI.saveDraft({
          policy,
          basedOnVersion,
          expectedDraftVersion,
          reason,
          idempotencyKey,
        });
        const confirmed = await refetchAfterMutation();
        if (!confirmed) setConfirmationStale(true);
        return { ok: true, confirmed };
      } catch (err) {
        const code = codeFromError(err);
        if (code === 409) {
          const confirmed = await refetchAfterMutation();
          if (!confirmed) setConfirmationStale(true);
        }
        const { message, errorCode } = extractPolicyApiError(
          err,
          'Failed to save draft',
        );
        return { ok: false, code, errors: [message], errorCode };
      }
    },
    [policyWire, confirmationStale, refetchAfterMutation],
  );

  const onActivate = useCallback(
    async (reason: string): Promise<SubmitResult> => {
      if (confirmationStale) {
        return {
          ok: false,
          code: 'network',
          errors: ['Reload the console before making another change.'],
        };
      }
      const idempotencyKey = createIdempotencyKey();
      const basedOnVersion = policyWire?.active?.version ?? '';
      const expectedDraftVersion = policyWire?.draft
        ? policyWire.draft.policy.version
        : null;
      try {
        await firstResponderConsoleAPI.activate({
          basedOnVersion,
          expectedDraftVersion,
          reason,
          idempotencyKey,
        });
        const confirmed = await refetchAfterMutation();
        if (!confirmed) setConfirmationStale(true);
        return { ok: true, confirmed };
      } catch (err) {
        const code = codeFromError(err);
        if (code === 409) {
          const confirmed = await refetchAfterMutation();
          if (!confirmed) setConfirmationStale(true);
        }
        const { message, errorCode } = extractPolicyApiError(
          err,
          'Failed to activate draft',
        );
        return { ok: false, code, errors: [message], errorCode };
      }
    },
    [policyWire, confirmationStale, refetchAfterMutation],
  );

  // M4: shares auditsRequestIdRef with load()/refetchAfterMutation() — a background refetch
  // racing this foreground page change (or two of either) can never have the earlier-started one
  // clobber the later one just because its network round trip happened to finish first.
  const onAuditPageChange = useCallback((page: number) => {
    setAuditPageNum(page);
    const requestId = (auditsRequestIdRef.current += 1);
    setAuditPageError(null);
    firstResponderConsoleAPI
      .getAudits(page, AUDITS_PER_PAGE)
      .then((wire) => {
        if (requestId !== auditsRequestIdRef.current) return;
        setAuditsWire(wire);
      })
      .catch(() => {
        // F8: was `.catch(() => {})` — a refused page change did nothing at all. The stale guard
        // applies to the FAILURE exactly as it does to the response: a superseded request's
        // failure must not raise an error over the page a newer request has since delivered.
        if (requestId !== auditsRequestIdRef.current) return;
        setAuditPageError("Couldn't load this page of changes.");
      });
  }, []);

  // ---------------------------------------------------------------------
  // Task 7 — real jobs, workers, attention, client filters.
  // ---------------------------------------------------------------------

  const fetchJobsPage = useCallback(
    async (customerId: number | null, page: number) => {
      const requestId = (jobsRequestIdRef.current += 1);
      const pageFetchId = (jobsPageFetchIdRef.current += 1);
      setJobsLoading(true);
      setJobsPageError(null);
      try {
        const wire = await firstResponderConsoleAPI.getJobs(
          customerId,
          page,
          JOBS_PER_PAGE,
        );
        // A newer request (from ANY of the three fetchers) has since started — this response is
        // stale even if it resolved first. Never let an out-of-order network reply overwrite what
        // the operator is now looking at.
        if (requestId !== jobsRequestIdRef.current) return;
        setJobsWire(wire);
      } catch {
        // F8: the previously-rendered page still stays (never blank the table over a transient
        // failure) — but silence was the bug: the spinner stopped, the old rows sat there, and the
        // click read as dead. Now it says so, with a Retry. The stale guard applies to the FAILURE
        // as well as the response: a superseded request's failure must not raise an error over the
        // page a newer request has since delivered.
        if (requestId !== jobsRequestIdRef.current) return;
        setJobsPageError("Couldn't load this page of jobs.");
      } finally {
        // jobsLoading depends ONLY on fetchJobsPage's own lifecycle (jobsPageFetchIdRef), never on
        // the shared cross-fetcher counter above — a background load()/refetchAfterMutation() must
        // never strand this flag true just because it (correctly) bumped the shared counter past
        // this call's id. Clear it exactly when no NEWER fetchJobsPage call has started since.
        if (pageFetchId === jobsPageFetchIdRef.current) setJobsLoading(false);
      }
    },
    [],
  );

  const onSelectFilter = useCallback(
    (key: string) => {
      const customerId: number | null =
        consoleWire?.filters?.find((f: any) => f.key === key)?.customer_id ??
        null;
      jobsQueryRef.current = { customerId, page: 1 };
      setSelectedFilter(key);
      setJobsPageNum(1); // changing the filter resets to page 1
      fetchJobsPage(customerId, 1);
    },
    [consoleWire, fetchJobsPage],
  );

  const onJobsPageChange = useCallback(
    (page: number) => {
      jobsQueryRef.current = { ...jobsQueryRef.current, page };
      setJobsPageNum(page);
      fetchJobsPage(jobsQueryRef.current.customerId, page);
    },
    [fetchJobsPage],
  );

  // F8: re-runs the EXACT fetch that failed. jobsQueryRef.current still holds the filter and page
  // the operator asked for (it is set before the request goes out, not after it succeeds), so
  // Retry is a genuine second attempt at that page — never a reload of the page still on screen.
  const onRetryJobsPage = useCallback(() => {
    const { customerId, page } = jobsQueryRef.current;
    fetchJobsPage(customerId, page);
  }, [fetchJobsPage]);

  // F8: same for the audit page — auditPageNum was set before the failed request, so this asks
  // for the page the operator clicked to, not the one whose rows are still displayed.
  const onRetryAuditPage = useCallback(() => {
    onAuditPageChange(auditPageNum);
  }, [auditPageNum, onAuditPageChange]);

  // Computed once per actual fetch (not per render) so a job's countdown secondsRemaining stays
  // stable while LiveConsole's own useCountdown ticker animates it.
  const nowMsForJobs = useMemo(() => Date.now(), [jobsWire]);

  const jobsPage = useMemo(
    () =>
      jobsWire
        ? {
            items: jobsWire.items.map((r: any) => toJobRow(r, nowMsForJobs)),
            pagination: toPagination(jobsWire.pagination),
          }
        : EMPTY_JOBS_PAGE,
    [jobsWire, nowMsForJobs],
  );

  // Filter chips come 1:1 from the console payload's own filters — the ONE pilot cohort list
  // (policyWire.active.clients) resolves a customer_id to a display name; no second customer list.
  const filters = useMemo(
    () =>
      consoleWire
        ? toClientFilters(
            consoleWire.filters,
            policyWire?.active?.clients ?? [],
          )
        : [],
    [consoleWire, policyWire],
  );

  // The Needs Attention CARD — from the real attention endpoint, never derived from job rows.
  const attention = useMemo(
    () => (attentionWire ? toAttentionPage(attentionWire) : null),
    [attentionWire],
  );

  const onSelectJob = useCallback(
    (id: string | null, section: 'overview' | 'workers' = 'overview') => {
      setWorkersPageNum(1);
      setWorkersPage(null);
      setOpenJob(id === null ? null : { id, section });
    },
    [],
  );

  // Job detail: getJob(jobId), cached by id. Only ever called for a job id JobsCard actually let
  // the operator click — a real unrecorded 'watching' row is never clickable (see toJobRow's
  // `clickable`), so this never has to survive a 404 for one.
  //
  // M1: a failure used to be a silent dead click (empty catch — no modal, no feedback). Now sets
  // jobLoadError, surfaced as a small inline retry notice in the jobs card (never a blank modal).
  // jobLoadRetryTick forces a re-run on Retry even though openJob/jobDetailsById are unchanged.
  useEffect(() => {
    if (!openJob) {
      setJobLoadError(null);
      return undefined;
    }
    // ALWAYS refetch on open (review round 1). A cached copy used to be served for the life of
    // the page, so once a tick moved a run on, the list row read NEEDS ATTENTION while the modal
    // still read WOULD OFFER, and Retry hit the same cache guard. A cached copy still renders at
    // once; the fresh one replaces it when it lands. A failed refresh keeps the cached copy
    // rather than swapping a real, if older, evaluation for an error.
    let cancelled = false;
    setJobLoadError(null);
    firstResponderConsoleAPI
      .getJob(openJob.id)
      .then((wire) => {
        if (cancelled) return;
        setJobDetailsById((prev) => ({
          ...prev,
          [openJob.id]: toJobDetail(wire),
        }));
      })
      .catch(() => {
        if (cancelled) return;
        if (jobDetailsRef.current[openJob.id]) return;
        setJobLoadError("Couldn't load this job's evaluation.");
      });
    return () => {
      cancelled = true;
    };
    // jobDetailsById is read through jobDetailsRef so that storing the fresh copy cannot re-run
    // this effect; jobDetailsVersion is bumped by refetchAfterMutation so an OPEN modal follows
    // the tick that just ran instead of waiting for the next open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openJob, jobLoadRetryTick, jobDetailsVersion]);

  // Workers tab: getWorkers(jobId, page, perPage) with real server paging, refetched whenever the
  // open job or the workers page number changes.
  //
  // M1: a failure used to be silent (empty catch — JobModal simply never renders since it
  // requires workersPage too, an equally silent dead click). Now sets the same jobLoadError.
  useEffect(() => {
    if (!openJob) return undefined;
    let cancelled = false;
    // Cleared unconditionally on every run (unlike the job-detail effect above, this one has no
    // cache guard to skip): a retry must be able to clear a workers-only failure even though the
    // job-detail effect's own cache guard leaves ITS state untouched when detail already loaded.
    setJobLoadError(null);
    firstResponderConsoleAPI
      .getWorkers(openJob.id, workersPageNum, WORKERS_PER_PAGE)
      .then((wire) => {
        if (cancelled) return;
        setWorkersPage(toWorkersPage(wire));
      })
      .catch(() => {
        if (cancelled) return;
        setJobLoadError("Couldn't load this job's evaluation.");
      });
    return () => {
      cancelled = true;
    };
  }, [openJob, workersPageNum, jobLoadRetryTick]);

  const onWorkersPageChange = useCallback((page: number) => {
    setWorkersPageNum(page);
  }, []);

  // M1: re-runs both the job-detail and workers effects above. The job-detail effect's own cache
  // guard (`jobDetailsById[openJob.id]`) means a retry never re-fetches a call that already
  // succeeded — only whichever of the two actually failed does real work again.
  const onRetryJobLoad = useCallback(() => {
    setJobLoadRetryTick((t) => t + 1);
  }, []);

  // ---------------------------------------------------------------------
  // Task 3 — mode/stop via the real mode endpoint. Never optimistic: only ever reflects a
  // backend-confirmed refetch; a failure leaves the prior consoleWire (and thus displayed mode)
  // untouched and surfaces the real message via bannerSlot. Every transition — including Stop —
  // requires a non-blank operator-typed reason, collected by ModeReasonModal/StopConfirmModal.
  // ---------------------------------------------------------------------

  // I3: mirrors onSaveDraft/onActivate's own stale-confirmation refusal — a mode transition must
  // not be attempted while the last confirmed state is unknown (confirmationStale). Previously
  // this function had no such guard (unlike Save Draft/Activate), so a mode POST could go out
  // against state the operator can no longer trust.
  //
  // CRITICAL correction: the guard must NEVER apply to 'off'. Stop is the SAFER action — the
  // codebase's own invariant (see LiveConsole.tsx's control-availability comment) is that a
  // safety/confirmation failure must never remove it. Gating Stop on confirmationStale meant
  // that after any single failed post-mutation refetch (e.g. a successful Go Live whose own
  // confirming refetch then failed), the console would render Stop as enabled while silently
  // refusing to actually post it — the pilot keeps running while the console implies it's under
  // control. Only 'shadow'/'live' (escalating or re-arming) stay gated.
  const submitMode = useCallback(
    async (
      mode: 'off' | 'shadow' | 'live',
      reason: string,
      failureContext: string,
    ) => {
      if (confirmationStale && mode !== 'off') {
        setModeError('Reload the console before making another change.');
        return;
      }
      setModePending(true);
      setModeError(null);
      try {
        await firstResponderConsoleAPI.setMode({ mode, reason });
        const confirmed = await refetchAfterMutation();
        if (!confirmed) setConfirmationStale(true);
      } catch (err) {
        setModeError(extractPolicyApiError(err, failureContext).message);
      } finally {
        setModePending(false);
      }
    },
    [confirmationStale, refetchAfterMutation],
  );

  // Opens the reason modal for the target ConsoleHeader's Start Shadow / Go Live / Back to
  // Shadow button legally offered — the actual POST happens on confirm, once a reason exists.
  const onRequestModeTransition = useCallback((target: ModeReasonTarget) => {
    setModeReasonTarget(target);
  }, []);

  const onConfirmModeReason = useCallback(
    (reason: string) => {
      const target = modeReasonTarget;
      if (!target) return;
      setModeReasonTarget(null);
      submitMode(
        target,
        reason,
        target === 'live' ? 'Failed to go live' : 'Failed to switch to Shadow',
      );
    },
    [modeReasonTarget, submitMode],
  );

  const onCancelModeReason = useCallback(() => setModeReasonTarget(null), []);

  // Cohort = the active pilot config's client list (policyWire.active.clients) — the same
  // customer_ids the backend resolves via FirstResponder::PilotScope.resolve. No second list.
  const cohortClients = useMemo(
    () =>
      policyWire?.active
        ? policyWire.active.clients.map((c) => ({
            customerId: c.customer_id,
            name: c.name,
          }))
        : [],
    [policyWire],
  );

  const onConfirmStop = useCallback(
    (reason: string) => {
      setStopOpen(false);
      submitMode('off', reason, 'Failed to stop First Responder');
    },
    [submitMode],
  );

  // F11 — "Run one tick now". Same audited endpoint the scheduler and the legacy Desktop
  // 2-tick.sh script hit, POSTed with no body (the mode is read from the two feature flags
  // server-side, never named by the caller). A halted tick is a truthful result, not a failure
  // (the kill switch being off, or the allowlist being empty, is the system working) — so it's
  // reported via halt_reason, not the catch block. Only a real request failure (network, 5xx,
  // etc.) goes through the catch, with the same extractPolicyApiError mapping submitMode uses.
  const onRunTick = useCallback(async () => {
    setRunTickPending(true);
    try {
      const result = await firstResponderConsoleAPI.runTick();
      setRunTickResult(
        result.halt_reason
          ? `Tick halted: ${result.halt_reason}`
          : `Tick ran: discovered ${result.discovered}, evaluated ${result.evaluated}`,
      );
      const confirmed = await refetchAfterMutation();
      if (!confirmed) setConfirmationStale(true);
    } catch (err) {
      setRunTickResult(
        `Tick failed: ${extractPolicyApiError(err, 'Failed to run tick').message}`,
      );
    } finally {
      setRunTickPending(false);
    }
  }, [refetchAfterMutation]);

  // F9: mode-aware Stop copy, read from consoleWire directly (not data.vm.requestedMode) so it
  // is defined even in the 'loading'/'error'/'unauthorized' states — LiveConsole.tsx always needs
  // a stopConfirmCopy, and Stop is reachable from the 'error' status too (see its own doc comment
  // on stopEnabled). An unknown mode (no consoleWire yet) falls back to the Shadow/OFF variant —
  // the same "today's copy stays" default as OFF itself, never the more permissive LIVE claim.
  const stopConfirmCopy: LiveStopCopy =
    consoleWire?.mode?.requested_mode === 'live'
      ? REAL_STOP_COPY_LIVE
      : REAL_STOP_COPY_SHADOW;

  // ---------------------------------------------------------------------
  // View model assembly
  // ---------------------------------------------------------------------

  const data: LiveConsoleData = (() => {
    if (status === 'loading') return { status: 'loading' };
    if (status === 'unauthorized') return { status: 'unauthorized' };
    if (status === 'error') return { status: 'error', message: errorMessage };
    const stats = toStats(consoleWire);
    return {
      status: 'ready',
      jobsLoading,
      vm: {
        // C1: carry BOTH — requestedMode is what is armed (button set, Stop); effectiveMode is
        // what the guards actually permitted (used only to detect a gate-halted mode for the
        // badge's "· halted" marker). Never collapse the two back into one value here.
        requestedMode: consoleWire.mode.requested_mode,
        effectiveMode: consoleWire.mode.effective_mode,
        safety: toSafety(consoleWire.safety),
        // needsAttention deliberately does NOT come from toStats's own stats.needs_attention — it
        // is the console's dedicated attention_count field (null renders Unknown, never 0), a
        // distinct claim from the attention CARD's own count (below), which is the real,
        // open-filtered attention collection.
        stats: {
          ...stats,
          needsAttention: {
            value: consoleWire.attention_count,
            windowLabel: 'unresolved',
          },
        },
        attention,
        filters,
        ranking: REAL_RANKING_EXPLAINER,
        detailsById: jobDetailsById,
        results: toResults(consoleWire),
        diagnostics: toDiagnostics(consoleWire),
        stopConfirm: stopConfirmCopy,
        // Naming debt: liveViewModel.ts's field is still called `fixtureNote` (a LivePreview-era
        // name never generalized in this convergence — tracked, not hidden, in progress.md).
        fixtureNote: 'Connected to the real First Responder backend.',
      },
    };
  })();

  // GRAVY-10373 Task 8: a safety failure locks configuration and nothing else — but an editor
  // that was already open when the backend's verdict changed must not survive it. The console
  // withholds the editor slot in that state; this closes the open state behind it, so a refetch
  // that reports safety clear again never silently reopens a half-finished edit.
  const safetyState = data.status === 'ready' ? data.vm.safety.state : null;
  useEffect(() => {
    if (safetyState !== null && safetyState !== 'clear') setEditorOpen(false);
  }, [safetyState]);

  const settings: SettingsProps = {
    // No active policy (nothing provisioned yet) is a real state the page must render. An empty
    // object cast as a policy crashed every reader of it in production (TENANT-ADMIN-PROD-8T).
    active: policyWire?.active ? mapPolicy(policyWire).active! : null,
    draft: policyWire ? mapPolicy(policyWire).draft : null,
    auditPage: auditsWire
      ? mapPolicyAudits(auditsWire)
      : {
          items: [],
          pagination: {
            total: 0,
            page: 1,
            perPage: AUDITS_PER_PAGE,
            totalPages: 0,
          },
        },
    editorOpen,
    onOpenEditor: () => setEditorOpen(true),
    onCloseEditor: () => setEditorOpen(false),
    onAuditPageChange,
    auditError: auditPageError
      ? { message: auditPageError, onRetry: onRetryAuditPage }
      : null,
  };

  // I3: modeError takes precedence over the stale banner when both exist — it is the more
  // recent, actionable message (previously `confirmationStale ? staleText : modeError` let a
  // lingering stale flag permanently hide a real, just-received backend error). The stale
  // headline still shows, as a second line, whenever both are present.
  const banner = (confirmationStale || modeError) && (
    <div
      className={`${styles.card} ${styles.attention}`}
      role="alert"
      data-testid={modeError ? 'flp-mode-error' : 'flp-confirmation-stale'}
      style={{ margin: '0 0 16px' }}
    >
      <div className={styles.section}>
        <div className={styles.strong}>
          {modeError ??
            'The last change may have saved, but the latest state could not be confirmed.'}
        </div>
        <div
          className={`${styles.muted} ${styles.small}`}
          style={{ marginTop: 4 }}
        >
          {modeError
            ? confirmationStale
              ? 'The last change may have saved, but the latest state could not be confirmed.'
              : 'The prior state is still shown — nothing was changed.'
            : 'Reload before making another change.'}
        </div>
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnOutline}`}
            data-testid="flp-confirmation-reload"
            onClick={() => {
              setModeError(null);
              load(auditPageNum);
            }}
          >
            Reload
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <LiveConsole
      data={data}
      jobsPage={jobsPage}
      selectedFilter={selectedFilter}
      onSelectFilter={onSelectFilter}
      onJobsPageChange={onJobsPageChange}
      openJob={openJob}
      workersPage={openJob ? workersPage : null}
      onWorkersPageChange={onWorkersPageChange}
      jobsLoadError={
        jobLoadError ? { message: jobLoadError, onRetry: onRetryJobLoad } : null
      }
      // F8: deliberately a SECOND notice, not a reuse of jobsLoadError above — the two failures
      // are different claims with different retries (one re-opens a job, one re-requests a page of
      // the table), and collapsing them would make Retry ambiguous exactly when it matters.
      jobsPageError={
        jobsPageError
          ? { message: jobsPageError, onRetry: onRetryJobsPage }
          : null
      }
      unauthorizedReason={unauthorizedReason}
      stopConfirmCopy={stopConfirmCopy}
      stopOpen={stopOpen}
      settings={settings}
      settingsEditorSlot={
        data.status === 'ready' && policyWire?.active ? (
          <ConnectedPolicyEditor
            active={mapPolicy(policyWire).active!}
            draft={mapPolicy(policyWire).draft}
            constraints={policyWire.constraints}
            // C1 follow-up: the backend's PolicyActivation judges "is First Responder off"
            // against the CONFIGURED intent (requested_mode), not the guard-derived effective
            // mode — feeding effectiveMode here would offer Activate on a gate-halted SHADOW
            // (requested shadow, effective off), a click the backend would refuse.
            mode={data.vm.requestedMode}
            blockedByStaleConfirmation={confirmationStale}
            safetyBlocksEdits={data.vm.safety.state !== 'clear'}
            onSaveDraft={onSaveDraft}
            onActivate={onActivate}
            onClose={() => setEditorOpen(false)}
          />
        ) : null
      }
      bannerSlot={banner || null}
      onSelectJob={onSelectJob}
      onRequestModeTransition={onRequestModeTransition}
      modeReasonTarget={modeReasonTarget}
      cohortClients={cohortClients}
      onConfirmModeReason={onConfirmModeReason}
      onCancelModeReason={onCancelModeReason}
      modePending={modePending}
      onRequestStop={() => setStopOpen(true)}
      onConfirmStop={onConfirmStop}
      onCancelStop={() => setStopOpen(false)}
      onRetry={() => load(auditPageNum)}
      onRunTick={onRunTick}
      runTickPending={runTickPending}
      runTickResult={runTickResult}
      canOperate={canOperate}
    />
  );
};

export default ConnectedFirstResponder;
